import React, { useState } from 'react';
import { MaskedInput } from 'antd-mask-input';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Input, Form, Row, DatePicker, Col, Divider } from 'antd';
import {
  DrawerEdit,
  ResourceEdit,
  FormItemWrap,
  normalizeFromSelect,
  normalizeToSelect
} from 'react-structure-admin';
import { currentUserUpdate } from 'stores/users/userActions';
import { mobilePhoneValidator } from 'util/validators/index';
import {
  ResCol,
  SelectGender,
  SelectProfessionalRegistrationTypes,
  SelectStates,
  UploadProfileImage
} from 'components';
import {
  cancelCurrentProfessional,
  updateCurrentProfessional
} from 'stores/healthServiceProviders/healthServiceProviderActions';
import RemoteSelect from 'components/RemoteSelect';

const CurrentProfessionalForm = (props) => {
  const { data, loading } = props;
  const dispatch = useDispatch();
  const [registrationType, setRegistrationType] = useState(null);

  const handleBeforeSubmit = ({
    birthDate,
    medicalSpecialties = [],
    address,
    ...rest
  }) => {
    return {
      ...rest,
      medicalSpecialties: medicalSpecialties.map((c) => ({
        medicalSpecialty: normalizeFromSelect(c)
      })),
      birthDate: birthDate ? moment(birthDate).format('YYYY/MM/DD') : null
    };
  };

  const handleBeforeBinding = ({
    birthDate,
    medicalSpecialties = [],
    address = {},
    ...rest
  }) => {
    return {
      ...rest,
      medicalSpecialties: medicalSpecialties.map((c) =>
        normalizeToSelect(c.medicalSpecialty)
      ),
      birthDate: birthDate ? moment(birthDate) : null
    };
  };

  const handleSubmit = (data) => {
    const values = handleBeforeSubmit(data);
    dispatch(updateCurrentProfessional(values));
  };

  const handleCancel = () => {
    dispatch(cancelCurrentProfessional());
  };

  const handleValuesChange = (_, { professionalRegistration }) => {
    setRegistrationType(professionalRegistration?.type ?? null);
  };

  return (
    <DrawerEdit
      {...props}
      data={data}
      title="Meus dados profissionais"
      saving={loading}
      onSubmit={handleSubmit}
      cancelEdit={handleCancel}
      onBeforeBinding={handleBeforeBinding}
      onValuesChange={handleValuesChange}
      size="md"
    >
      <Form>
        <Row>
          <Col span={18}>
            <FormItemWrap name="name" label="Nome" required>
              <Input maxLength={100} />
            </FormItemWrap>
            <Row className="no-padding-last">
              <Col span={12}>
                <FormItemWrap name="gender" label="Sexo" required>
                  <SelectGender />
                </FormItemWrap>
              </Col>
              <Col span={12}>
                <FormItemWrap name="birthDate" label="Data de nascimento">
                  <DatePicker
                    format="DD/MM/YYYY"
                    disabledDate={(date) => moment(date).isAfter(moment())}
                  />
                </FormItemWrap>
              </Col>
            </Row>
          </Col>
          <Col span={6}>
            <Form.Item name="profileImage" noStyle>
              <UploadProfileImage resource="health-service-providers/profile-image" />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <FormItemWrap
              name="mobilePhone"
              label="Celular"
              required
              rules={[{ validator: mobilePhoneValidator }]}
            >
              <MaskedInput mask="(11) 11111-1111" />
            </FormItemWrap>
          </Col>
          <Col span={12}>
            <FormItemWrap
              name="landlinePhone"
              label="Telefone fixo"
              rules={[{ validator: mobilePhoneValidator }]}
            >
              <MaskedInput mask="(11) 1111-1111" />
            </FormItemWrap>
          </Col>
        </Row>

        <Row>
          <Col span={12}>
            <FormItemWrap name="email" label="E-mail" required type="email">
              <Input maxLength={80} />
            </FormItemWrap>
          </Col>
          <Col span={12}>
            <FormItemWrap name="website" label="Site">
              <Input maxLength={120} />
            </FormItemWrap>
          </Col>
        </Row>

        <Divider orientation="left">Informações profissionais</Divider>
        <Row>
          <ResCol span={11}>
            <FormItemWrap
              name={['professionalRegistration', 'type']}
              label="Conselho"
            >
              <SelectProfessionalRegistrationTypes />
            </FormItemWrap>
          </ResCol>
          <ResCol span={5}>
            <FormItemWrap
              name={['professionalRegistration', 'number']}
              label="Número"
            >
              <Input />
            </FormItemWrap>
          </ResCol>
          <ResCol span={8}>
            <FormItemWrap
              name={['professionalRegistration', 'state']}
              label="Estado"
            >
              <SelectStates />
            </FormItemWrap>
          </ResCol>
        </Row>

        <Row>
          <ResCol>
            <FormItemWrap name="medicalSpecialties" label="Especialidades">
              <RemoteSelect
                mode="multiple"
                resource="medical-specialties"
                params={{ registrationType }}
              />
            </FormItemWrap>
          </ResCol>
        </Row>
      </Form>
    </DrawerEdit>
  );
};

const ResourceComponent = ResourceEdit.create(CurrentProfessionalForm, {
  resource: 'health-service-providers',
  currentAttr: 'currentProfessional'
});

const CurrentProfessionalEdit = (props) => {
  const { editingCurrentProfessional } = useSelector(
    (state) => state.resources['health-service-providers'] || {}
  );

  if (!editingCurrentProfessional) {
    return null;
  }

  return <ResourceComponent {...props} visible />;
};

export default CurrentProfessionalEdit;
